import { Skeleton, Stack, Group, Card, Grid, Box } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { ReactNode } from "react";

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
}));

interface SkeletonLoaderProps {
  visible?: boolean;
  children?: ReactNode;
}

// Table row skeleton
export const TableRowSkeleton = ({ columns = 4 }: { columns?: number }) => (
  <Group justify="space-between" p="sm">
    {Array.from({ length: columns }).map((_, i) => (
      <Skeleton
        key={i}
        height={20}
        width={i === 0 ? "30%" : "15%"}
        radius="sm"
      />
    ))}
  </Group>
);

// Table skeleton with multiple rows
export const TableSkeleton = ({
  rows = 5,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}) => (
  <Stack gap={0}>
    {/* Header */}
    <Group
      justify="space-between"
      p="sm"
      style={{ borderBottom: "1px solid #373A40" }}
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          height={16}
          width={i === 0 ? "25%" : "12%"}
          radius="sm"
        />
      ))}
    </Group>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <TableRowSkeleton key={i} columns={columns} />
    ))}
  </Stack>
);

// Form field skeleton
export const FormFieldSkeleton = () => (
  <Stack gap="xs">
    <Skeleton height={14} width="20%" radius="sm" />
    <Skeleton height={36} radius="md" />
  </Stack>
);

// Form skeleton with multiple fields
export const FormSkeleton = ({ fields = 4 }: { fields?: number }) => (
  <Stack gap="md">
    {Array.from({ length: fields }).map((_, i) => (
      <FormFieldSkeleton key={i} />
    ))}
    <Skeleton height={36} width="30%" radius="md" mt="md" />
  </Stack>
);

// Card skeleton
export const CardSkeleton = () => {
  const { classes } = useStyles();
  return (
    <Card className={classes.card} p="lg" radius="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Skeleton height={24} width="40%" radius="sm" />
          <Skeleton height={24} width={24} radius="sm" />
        </Group>
        <Skeleton height={14} width="80%" radius="sm" />
        <Skeleton height={14} width="60%" radius="sm" />
      </Stack>
    </Card>
  );
};

// Stat card skeleton
export const StatCardSkeleton = () => {
  const { classes } = useStyles();
  return (
    <Card className={classes.card} p="md" radius="lg">
      <Stack gap="xs">
        <Skeleton height={12} width="50%" radius="sm" />
        <Skeleton height={32} width="60%" radius="sm" />
        <Skeleton height={10} width="40%" radius="sm" />
      </Stack>
    </Card>
  );
};

// Stats grid skeleton
export const StatsGridSkeleton = ({ count = 4 }: { count?: number }) => (
  <Grid>
    {Array.from({ length: count }).map((_, i) => (
      <Grid.Col key={i} span={{ base: 12, sm: 6, md: 3 }}>
        <StatCardSkeleton />
      </Grid.Col>
    ))}
  </Grid>
);

// Detail page skeleton
export const DetailPageSkeleton = () => (
  <Stack gap="lg">
    {/* Header */}
    <Group justify="space-between">
      <Stack gap="xs">
        <Skeleton height={28} width={300} radius="sm" />
        <Skeleton height={14} width={200} radius="sm" />
      </Stack>
      <Group>
        <Skeleton height={36} width={100} radius="md" />
        <Skeleton height={36} width={100} radius="md" />
      </Group>
    </Group>

    {/* Stats */}
    <StatsGridSkeleton count={4} />

    {/* Content */}
    <CardSkeleton />
    <CardSkeleton />
  </Stack>
);

// List page skeleton
export const ListPageSkeleton = () => (
  <Stack gap="md">
    {/* Header with button */}
    <Group justify="space-between">
      <Skeleton height={36} width={200} radius="md" />
      <Skeleton height={36} width={120} radius="md" />
    </Group>

    {/* Search/filter bar */}
    <Group>
      <Skeleton height={36} width={250} radius="md" />
      <Skeleton height={36} width={120} radius="md" />
    </Group>

    {/* Table */}
    <TableSkeleton rows={8} columns={5} />

    {/* Pagination */}
    <Group justify="space-between">
      <Skeleton height={20} width={100} radius="sm" />
      <Group gap="xs">
        <Skeleton height={32} width={32} radius="sm" />
        <Skeleton height={32} width={32} radius="sm" />
        <Skeleton height={32} width={32} radius="sm" />
      </Group>
    </Group>
  </Stack>
);

// Generic content skeleton with optional children
export const ContentSkeleton = ({
  visible = true,
  children,
}: SkeletonLoaderProps) => {
  if (!visible) {
    return <>{children}</>;
  }

  return (
    <Box>
      <Stack gap="md">
        <Skeleton height={24} width="60%" radius="sm" />
        <Skeleton height={14} width="100%" radius="sm" />
        <Skeleton height={14} width="90%" radius="sm" />
        <Skeleton height={14} width="80%" radius="sm" />
      </Stack>
    </Box>
  );
};

// Export all variants
export const SkeletonLoader = {
  Table: TableSkeleton,
  TableRow: TableRowSkeleton,
  Form: FormSkeleton,
  FormField: FormFieldSkeleton,
  Card: CardSkeleton,
  StatCard: StatCardSkeleton,
  StatsGrid: StatsGridSkeleton,
  DetailPage: DetailPageSkeleton,
  ListPage: ListPageSkeleton,
  Content: ContentSkeleton,
};
